/**
 * Call waiting: answering a call while already in one (web and native).
 *
 * The server answers call_waiting_respond with p2p_join_room for the new
 * call, and the queue middleware takes this device into that room. What
 * happens to the call this person is leaving depends on how they answered,
 * so the answer is remembered here until its p2p_join_room arrives:
 *
 * - 'accept' ("Answer"): the current call ends (call_end), as hanging up
 *   would, then the new one opens.
 * - 'hold_and_accept' ("Hold"): the server puts the current call on hold and
 *   tells the others in it. This device leaves its room without ending it,
 *   and Home offers it back ("On hold" · Resume), which takes it off hold.
 * - 'reject' ("Decline"): the caller is told; nothing else changes.
 */
/** An answer older than this no longer decides anything (the server replies in seconds). */
export const CALL_WAITING_ANSWER_TTL_MS = 60000;
/** How long an answer may go unconfirmed before the sheet offers a retry. */
export const CALL_WAITING_RESPONSE_TIMEOUT_MS = 10000;
let pendingAnswer;
export function rememberCallWaitingAnswer(answer, now = Date.now()) {
    pendingAnswer = { ...answer, at: now };
}
/**
 * The answer this device gave to `incomingCallId`, once: the p2p_join_room
 * for that call consumes it.
 */
export function takeCallWaitingAnswer(incomingCallId, now = Date.now()) {
    const answer = pendingAnswer;
    if (!answer || !incomingCallId || answer.incomingCallId !== incomingCallId) {
        return undefined;
    }
    pendingAnswer = undefined;
    return now - answer.at <= CALL_WAITING_ANSWER_TTL_MS ? answer : undefined;
}
/** The answer was refused or the ring ended: it decides nothing. */
export function forgetCallWaitingAnswer(incomingCallId) {
    if (!incomingCallId || pendingAnswer?.incomingCallId === incomingCallId) {
        pendingAnswer = undefined;
    }
}
/** What the others in a call see when someone holds it, or comes back. */
export function holdNoticeTitle(onHold, data) {
    const name = typeof data?.heldBy === 'string' && data.heldBy.trim() ? data.heldBy.trim() : '';
    if (onHold) {
        return name ? `${name} put the call on hold` : 'Call on hold';
    }
    return name ? `${name} is back` : 'Call resumed';
}
/** Short words for a refused answer, from the server's error code. */
export function callWaitingErrorMessage(code) {
    switch (code) {
        case 'CALL_FULL':
            return 'The call is full.';
        case 'CALL_ENDED':
        case 'NOT_FOUND':
        case 'INVITE_UNAVAILABLE':
            return 'That call has ended.';
        case 'CALL_NOT_HELD':
            return 'Your call could not be put on hold.';
        case 'CALL_NOT_HOLDABLE':
            return 'A call with an interpreter cannot be put on hold.';
        // One answer wins a ring (docs/single-winner-answers.md).
        case 'ANSWERED_ELSEWHERE':
        case 'ALREADY_TAKEN':
            return 'Answered on another device.';
        default:
            return 'Not answered. Try again.';
    }
}
/** The held call's row on Home, from its stored record. */
export function heldCallFromRecord(record, now = Date.now()) {
    const text = (value) => typeof value === 'string' && value.trim() ? value.trim() : '';
    const callId = text(record?.callId);
    const roomName = text(record?.roomName);
    if (!record || !callId || !roomName) {
        return null;
    }
    return {
        callId,
        heldAt: now,
        peerName: text(record.interpreterName) || text(record.clientName) || 'Call',
        record: { ...record },
        roomName
    };
}
